// Day 1 — "The Empty Stand"
// The proof-of-concept chapter that replaces the worksheet panels with story
// scenes. Five scenes in sequence; each dispatches engine actions when complete
// so the game state stays in lockstep with the narrative.

import { useState } from 'react';
import { IslandWakeUp } from './scenes/IslandWakeUp.js';
import { PriceChoice } from './scenes/PriceChoice.js';
import { TheDay } from './scenes/TheDay.js';

export type Day1Props = {
  /** Fires when Day 1 is complete — host swaps to the home screen. */
  onChapterEnd: () => void;
};

// Trimmed to a sub-10-second on-ramp: open the stand → pick a price → see the
// result → you're in the game. (The old post-day Reflect conversation + goodnight
// scenes were friction before any play; reflection happens later in the loop.)
type SceneKey = 'wakeUp' | 'priceChoice' | 'theDay';

const SEQUENCE: SceneKey[] = ['wakeUp', 'priceChoice', 'theDay'];

export function Day1({ onChapterEnd }: Day1Props) {
  const [idx, setIdx] = useState(0);
  const current = SEQUENCE[idx]!;

  const advance = () => {
    if (idx < SEQUENCE.length - 1) {
      setIdx(idx + 1);
    } else {
      onChapterEnd();
    }
  };

  // Plain conditional rendering for the top-level scene switch — each scene's
  // StoryShell fades itself in. (AnimatePresence mode="wait" around a keyed wrapper
  // can get stuck mid-exit and never mount the next scene — a bug we've hit before.)
  return (
    <>
      {current === 'wakeUp' && <IslandWakeUp onComplete={advance} />}
      {current === 'priceChoice' && <PriceChoice onComplete={advance} />}
      {current === 'theDay' && <TheDay onComplete={advance} />}
    </>
  );
}
